"use client";

import { apiFetch, API_URL } from "./api";
import type { AuthResponse, User } from "@/types";

const ACCESS_TOKEN_KEY = "cl_access_token";
const REFRESH_TOKEN_KEY = "cl_refresh_token";
const USER_KEY = "cl_user";

export function saveAuth(payload: AuthResponse) {
  localStorage.setItem(ACCESS_TOKEN_KEY, payload.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, payload.refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(payload.user));
}

export function clearAuth() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getStoredUser(): User | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as User;
  } catch {
    clearAuth();
    return null;
  }
}

export async function refreshTokens() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const payload = await apiFetch<AuthResponse>("/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refreshToken })
    });
    saveAuth(payload);
    return payload.accessToken;
  } catch {
    clearAuth();
    return null;
  }
}

export async function authFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  let accessToken = getAccessToken();
  const headers = new Headers(options.headers);
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  if (options.body && !headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  let response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers
  });

  if (response.status === 401) {
    accessToken = await refreshTokens();
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
      response = await fetch(`${API_URL}${path}`, {
        ...options,
        headers
      });
    }
  }

  if (!response.ok) {
    let message = "Yêu cầu không thành công";
    try {
      const payload = await response.json();
      message = Array.isArray(payload.message) ? payload.message.join(", ") : payload.message ?? message;
    } catch {
      message = response.statusText;
    }
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

export async function optionalAuthFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const accessToken = getAccessToken();
  const headers = new Headers(options.headers);
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  if (options.body && !headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  let response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    cache: options.cache ?? "no-store"
  });

  if (response.status === 401 && accessToken) {
    const refreshedToken = await refreshTokens();
    if (refreshedToken) {
      headers.set("Authorization", `Bearer ${refreshedToken}`);
      response = await fetch(`${API_URL}${path}`, {
        ...options,
        headers,
        cache: options.cache ?? "no-store"
      });
    }
  }

  if (!response.ok) {
    let message = "Không thể tải dữ liệu";
    try {
      const payload = await response.json();
      message = Array.isArray(payload.message) ? payload.message.join(", ") : payload.message ?? message;
    } catch {
      message = response.statusText;
    }
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}
